/**
 * adaptiveFps — target FPS 설정 해석 + auto 적응 결정 순수 코어. Phaser/DOM 의존 0.
 *
 * 왜 target FPS 캡이 필요한가: 120Hz 패널(예 갤럭시 S25 Ultra)에서 Phaser는 RAF로 주사율(120fps)을
 * 목표로 돌아 초당 120회 풀 파이프라인을 시도 → 과부하·발열·실측 프레임 추락. target을 60/30/15로
 * 캡하면 부하가 줄어 발열↓ + 프레임 안정. "auto"는 실측 fps로 단계를 자동 조정한다.
 *
 * 시간 입력은 dtMs로 주입받아(performance.now 미사용) 결정론·테스트 가능을 유지한다.
 */

export type TargetFpsSetting = "auto" | 15 | 30 | 60 | 120;

/** 설정 UI 노출 순서 (Auto가 첫 번째 = 권장). */
export const TARGET_FPS_OPTIONS: readonly TargetFpsSetting[] = ["auto", 15, 30, 60, 120] as const;

/** 적응 후보 단계 (오름차순). 기기 주사율 이하만 사용. */
export const DEFAULT_FPS_CANDIDATES: readonly number[] = [15, 30, 60, 120] as const;

export function isTargetFpsSetting(v: unknown): v is TargetFpsSetting {
  return v === "auto" || v === 15 || v === 30 || v === 60 || v === 120;
}

/** 기기 주사율 이하 후보만 추린다 (120Hz→[15,30,60,120], 60Hz→[15,30,60], 90Hz→[15,30,60]). */
export function resolveDisplayCandidates(
  displayHz: number,
  candidates: readonly number[] = DEFAULT_FPS_CANDIDATES,
): number[] {
  const usable = candidates.filter((c) => c <= displayHz);
  // 주사율이 최소 후보보다 낮아도(예 24Hz) 최소 1개는 보장.
  return usable.length > 0 ? usable : [candidates[0]];
}

/**
 * 설정값 → 부팅 시 적용할 초기 target FPS.
 * - 고정값(15/30/60/120): 기기 주사율로 clamp.
 * - "auto": 보수적으로 60에서 시작(가능 시) → 런타임 적응이 측정 후 올리거나 내린다.
 */
export function resolveTargetFps(
  setting: TargetFpsSetting,
  displayHz: number,
  candidates: readonly number[] = DEFAULT_FPS_CANDIDATES,
): number {
  const cands = resolveDisplayCandidates(displayHz, candidates);
  if (setting === "auto") {
    return cands.includes(60) ? 60 : cands[cands.length - 1];
  }
  return Math.min(setting, displayHz);
}

export interface AdaptiveFpsOptions {
  displayHz: number;
  /** 적응 후보 단계 (기본 [15,30,60,120]). */
  candidates?: readonly number[];
  /** 시작 target (기본 resolveTargetFps("auto", displayHz)). */
  start?: number;
  /** 실측 fps < target*downRatio = 저하 신호 (기본 0.85). */
  downRatio?: number;
  /** 실측 fps >= target*upRatio = 여유 신호 → 상위 단계 시도 (기본 0.95). */
  upRatio?: number;
  /** 신호가 이 시간(ms) 지속돼야 단계 변경 (기본 2000). */
  sustainMs?: number;
  /** 단계 변경 후 잠금 시간(ms) — 떨림 방지 (기본 4000). */
  cooldownMs?: number;
}

export interface AdaptiveFps {
  /** 현재 적용 target FPS. */
  current(): number;
  /**
   * dtMs 경과 + 실측 fps 관측 → 적용 target 반환(변화 없으면 동일값).
   * 저하 신호 지속 → 한 단계 강등 / 여유 신호 지속 → 한 단계 승급(못 버티면 다시 강등).
   */
  observe(measuredFps: number, dtMs: number): number;
}

/** auto 모드용 적응 컨트롤러. 고정 설정(15/30/60/120)에서는 사용하지 않는다. */
export function createAdaptiveFps(opts: AdaptiveFpsOptions): AdaptiveFps {
  const candidates = resolveDisplayCandidates(opts.displayHz, opts.candidates ?? DEFAULT_FPS_CANDIDATES);
  const downRatio = opts.downRatio ?? 0.85;
  const upRatio = opts.upRatio ?? 0.95;
  const sustainMs = opts.sustainMs ?? 2000;
  const cooldownMs = opts.cooldownMs ?? 4000;

  const start = opts.start ?? resolveTargetFps("auto", opts.displayHz, candidates);
  let idx = Math.max(0, candidates.indexOf(start));
  if (candidates.indexOf(start) === -1) {
    // start가 후보에 없으면 가장 가까운(이하) 단계로.
    idx = candidates.reduce((best, c, i) => (c <= start ? i : best), 0);
  }
  let downMs = 0;
  let upMs = 0;
  let lockMs = 0;

  function observe(measuredFps: number, dtMs: number): number {
    if (lockMs > 0) {
      lockMs = Math.max(0, lockMs - dtMs);
      return candidates[idx];
    }
    const target = candidates[idx];
    if (measuredFps < target * downRatio) {
      downMs += dtMs;
      upMs = 0;
      if (downMs >= sustainMs && idx > 0) {
        idx -= 1;
        downMs = 0;
        lockMs = cooldownMs;
      }
    } else if (idx + 1 < candidates.length && measuredFps >= target * upRatio) {
      upMs += dtMs;
      downMs = 0;
      if (upMs >= sustainMs) {
        idx += 1;
        upMs = 0;
        lockMs = cooldownMs;
      }
    } else {
      downMs = 0;
      upMs = 0;
    }
    return candidates[idx];
  }

  return { current: () => candidates[idx], observe };
}
